import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DateTime } from "luxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { FilterDropdown } from "/imports/ui/components/shared";
import { dayLabel, weekLabel } from "../fullScheduleHelpers";

const pillGroupSx = {
  bgcolor: "action.hover",
  borderRadius: 999,
  p: 0.5,
  gap: 0.5,
  "& .MuiToggleButtonGroup-grouped": {
    border: 0,
    borderRadius: "999px !important",
    px: 1.5,
    py: 0.25,
    color: "text.secondary",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { bgcolor: "transparent", color: "text.primary" },
    "&.Mui-selected": {
      bgcolor: "primary.main",
      color: "primary.contrastText",
      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      "&:hover": { bgcolor: "primary.dark", color: "primary.contrastText" }
    }
  }
};

// The partners calendar filters, compacted for a phone: location + entity
// dropdowns, Trainers|Courts and Day|Week pill pairs, and a prev/today/next
// date strip with a tap-to-pick label. Week stays disabled until a specific
// entity is chosen ("all" is day-only, like partners).
function FilterBar({ filters, locations, roster, loading, onLocation, onEntityType, onEntityId, onView, onDate, onShiftDate, onToday }) {
  const isWeek = filters.view === "week";
  const label = isWeek ? weekLabel(filters.weekStart, filters.weekEnd) : dayLabel(filters.date);
  const dateValue = filters.date ? DateTime.fromISO(filters.date, { zone: "UTC" }) : null;

  return (
    <Card sx={{ mb: 1.5 }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.25, py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <FilterDropdown
            label="Location"
            options={locations.map((l) => ({ value: l._id, label: l.name }))}
            value={filters.locationId}
            onChange={(v) => v && onLocation(v)}
            allowAll={false}
            fullWidth
            minWidth={0}
          />
          <FilterDropdown
            label={filters.entityType === "facility" ? "Court" : "Trainer"}
            options={roster.map((r) => ({ value: r._id, label: r.name }))}
            value={filters.entityId === "all" ? null : filters.entityId}
            onChange={(v) => onEntityId(v || "all")}
            allLabel="All"
            fullWidth
            minWidth={0}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <ToggleButtonGroup size="small" exclusive value={filters.entityType} onChange={(_, v) => v && onEntityType(v)} sx={pillGroupSx}>
            <ToggleButton value="trainer">Trainers</ToggleButton>
            <ToggleButton value="facility">Courts</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" exclusive value={filters.view} onChange={(_, v) => v && onView(v)} sx={pillGroupSx}>
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="week" disabled={filters.entityId === "all" && !roster.length}>
              Week
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
          <IconButton size="small" onClick={() => onShiftDate(-1)} disabled={loading}>
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0 }}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <MobileDatePicker
                value={dateValue}
                onChange={(dt) => dt && onDate(dt.toFormat("yyyy-MM-dd"))}
                format="dd LLL yyyy"
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "standard",
                    InputProps: {
                      disableUnderline: true,
                      sx: { fontWeight: 600, fontSize: "0.9375rem", cursor: "pointer", input: { textAlign: "center", cursor: "pointer" } }
                    },
                    // The picker owns the value; show our own day/week label.
                    inputProps: { value: label || "Pick a date" }
                  }
                }}
                sx={{ width: "10.625rem" }}
              />
            </LocalizationProvider>
            <Button size="small" onClick={onToday} disabled={loading} sx={{ minWidth: 0, py: 0, lineHeight: 1.5 }}>
              Today
            </Button>
          </Box>
          <IconButton size="small" onClick={() => onShiftDate(1)} disabled={loading}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
}

export default FilterBar;
